// Stripe Checkout launcher. Calls a Supabase edge function that creates a
// Checkout session for the requested SKU and returns the redirect URL.
//
// We don't load @stripe/stripe-js client-side because we only need the
// redirect URL — there's no need to ship the full Stripe.js bundle to do that.
//
// The corresponding edge function (`checkout`) is part of P3 backend work and
// lives in backend/supabase/functions/checkout/.

import { supabase, SUPABASE_FN_URL } from '../auth/supabase';

export type CheckoutSku = 'monthly' | 'annual' | 'bundle_10' | 'bundle_100';

export async function startCheckout(sku: CheckoutSku): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  const base = import.meta.env.BASE_URL; // '/cv-web/' in prod, '/' in dev
  const successUrl = `${window.location.origin}${base}?checkout=success`;
  const cancelUrl  = `${window.location.origin}${base}pricing?checkout=cancel`;

  const res = await fetch(`${SUPABASE_FN_URL}/checkout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sku, success_url: successUrl, cancel_url: cancelUrl }),
  });
  if (!res.ok) throw new Error(`checkout HTTP ${res.status}`);
  const { url } = await res.json() as { url: string };
  if (!url) throw new Error('checkout returned no url');
  window.location.assign(url);
}

export class PortalError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message?: string) {
    super(message ?? code);
    this.code = code;
    this.status = status;
  }
}

export async function openCustomerPortal(): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new PortalError('not_signed_in', 401);
  const res = await fetch(`${SUPABASE_FN_URL}/customer-portal`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ return_url: window.location.origin }),
  });
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body?.error) code = body.error;
    } catch { /* ignore */ }
    throw new PortalError(code, res.status);
  }
  const { url } = await res.json() as { url: string };
  if (!url) throw new PortalError('no_url', 500);
  window.location.assign(url);
}
